import type { StripeCheckoutSession } from '@stripe/stripe-js';
import { Link } from 'react-router';

import { Skeleton } from '@/components/ui/skeleton';
import { PRICING_ROUTE } from '@/lib/onboarding-funnel';

const INTERVAL_LABEL: Record<string, string> = {
    day: 'daily',
    week: 'weekly',
    month: 'monthly',
    year: 'yearly',
};

/**
 * The order summary: Stripe's arithmetic, our markup. Until a billing address
 * exists automatic tax cannot be computed (`checkout.tax.status` says so) and
 * the total reads "+ tax".
 */
export function OrderSummary({ checkout }: { checkout: StripeCheckoutSession | null }) {
    return (
        <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
                <h2 className="m-0 text-[15px] font-semibold text-foreground">Order summary</h2>
                <Link to={PRICING_ROUTE} className="text-[13px] font-medium text-primary">
                    Change plan
                </Link>
            </div>
            {checkout === null ? (
                <div data-testid="order-summary-loading" className="flex flex-col gap-2.5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                </div>
            ) : (
                <Rows checkout={checkout} />
            )}
        </div>
    );
}

function Rows({ checkout }: { checkout: StripeCheckoutSession }) {
    const interval = checkout.recurring?.interval ?? 'month';
    const taxReady = checkout.tax.status === 'ready';

    return (
        <div className="flex flex-col gap-2 text-sm">
            {checkout.lineItems.map((item) => (
                <Row
                    key={item.id}
                    label={`${item.name} · ${INTERVAL_LABEL[item.recurring?.interval ?? interval] ?? interval}`}
                    value={item.total.amount}
                />
            ))}
            {taxReady && checkout.taxAmounts && checkout.taxAmounts.length > 0 ? (
                checkout.taxAmounts.map((tax, index) => (
                    <Row
                        key={`${tax.displayName}-${index}`}
                        label={`${tax.displayName}${tax.percentage !== undefined ? ` ${tax.percentage}%` : ''}`}
                        value={tax.amount}
                        muted
                    />
                ))
            ) : (
                <Row label="VAT (once address is known)" value="–" muted />
            )}
            <div className="my-1 border-t border-border" />
            <Row
                label={`Total per ${interval}`}
                value={taxReady ? checkout.total.total.amount : `${checkout.total.subtotal.amount} + tax`}
                strong
            />
        </div>
    );
}

function Row({
    label,
    value,
    muted,
    strong,
}: {
    label: string;
    value: string;
    muted?: boolean;
    strong?: boolean;
}) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className={muted ? 'text-muted-foreground' : 'text-foreground'}>{label}</span>
            <span
                className={
                    strong
                        ? 'font-semibold text-foreground'
                        : muted
                          ? 'text-muted-foreground'
                          : 'text-foreground'
                }
            >
                {value}
            </span>
        </div>
    );
}
